import { useState } from 'react'
import { supabase } from '../lib/supabase'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

// Admin bulk email form. Audience is fixed on the backend: users who said
// they want to earn (or both), have no listings, and haven't unsubscribed.
// The campaign name is what stops repeat sends — reuse it to top up people
// who joined since, use a new one for a new email.
export default function AdminBulkEmail() {
const [campaignKey, setCampaignKey] = useState('')
const [subject, setSubject] = useState('')
const [body, setBody] = useState('')
const [preview, setPreview] = useState(null)
const [confirming, setConfirming] = useState(false)
const [busy, setBusy] = useState('')
const [message, setMessage] = useState(null)

const call = async (payload) => {
const { data: { session } } = await supabase.auth.getSession()
const res = await fetch(`${BACKEND_URL}/admin/bulk-email`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
body: JSON.stringify({ campaignKey: campaignKey.trim(), subject, body, ...payload }),
})
const json = await res.json().catch(() => ({}))
if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`)
return json
}

const run = async (label, fn) => {
setBusy(label)
setMessage(null)
try {
await fn()
} catch (err) {
setMessage({ type: 'error', text: err.message })
} finally {
setBusy('')
}
}

const handlePreview = () => run('preview', async () => {
setConfirming(false)
const result = await call({ mode: 'preview' })
setPreview(result)
})

const handleTest = () => run('test', async () => {
const result = await call({ mode: 'test' })
setMessage({ type: 'success', text: `Test sent to ${result.sentTo}. Check the wording and links before sending to everyone (don't click Unsubscribe in the test).` })
})

const handleSend = () => run('send', async () => {
const result = await call({ mode: 'send', expectedCount: preview?.count })
setConfirming(false)
setPreview(null)
setMessage({
type: 'success',
text: result.started
? `Sending to ${result.total} people now. It runs in the background (about 1–2 per second) — click Preview to check progress.`
: 'Nobody left to send to for this campaign.',
})
})

const canSendContent = campaignKey.trim() && subject.trim() && body.trim()

return (
<div className="card">
<div className="card-body">
<h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>📧 Bulk email: "List your item"</h3>
<p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
Goes to users who chose <strong>earn</strong> or <strong>both</strong> at sign-up, have no listings, and haven't unsubscribed. Admins are excluded. An unsubscribe link is added to the footer automatically.
</p>

<div className="form-group">
<label className="form-label">Campaign name</label>
<input
className="form-control"
placeholder="e.g. list-your-item-oct-2026"
value={campaignKey}
onChange={e => { setCampaignKey(e.target.value.toLowerCase()); setPreview(null); setConfirming(false) }}
/>
<div className="form-hint">Lowercase letters, numbers and dashes. Nobody gets the same campaign name twice.</div>
</div>

<div className="form-group">
<label className="form-label">Subject</label>
<input className="form-control" value={subject} maxLength={150} onChange={e => setSubject(e.target.value)} />
</div>

<div className="form-group">
<label className="form-label">Email body</label>
<textarea
className="form-control"
rows={10}
value={body}
onChange={e => setBody(e.target.value)}
placeholder={'Hi {first_name},\n\nYour message here...\n\nCheers,\nKy'}
style={{ fontFamily: 'inherit', resize: 'vertical' }}
/>
<div className="form-hint">Plain text. Use {'{first_name}'} for the person's first name. Leave a blank line between paragraphs.</div>
</div>

{message && <div className={`alert alert-${message.type}`} style={{ marginBottom: 12 }}>{message.text}</div>}

{preview && (
<div style={{ background: 'var(--gray-50)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 14px', marginBottom: 12, fontSize: 13 }}>
<div style={{ fontWeight: 700, color: 'var(--navy)', marginBottom: 4 }}>
{preview.count} {preview.count === 1 ? 'person' : 'people'} will receive this
</div>
{preview.alreadySent > 0 && <div style={{ color: 'var(--text-muted)' }}>{preview.alreadySent} already received this campaign (skipped)</div>}
{preview.running && <div style={{ color: 'var(--amber)' }}>A send is still running in the background</div>}
{preview.sample.length > 0 && <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>e.g. {preview.sample.join(', ')}</div>}
</div>
)}

{confirming ? (
<div className="alert alert-warn" style={{ marginBottom: 0 }}>
<div style={{ fontWeight: 700, marginBottom: 8 }}>Send "{subject}" to {preview.count} {preview.count === 1 ? 'person' : 'people'}? This can't be undone.</div>
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
<button className="btn btn-danger btn-sm" disabled={!!busy} onClick={handleSend}>
{busy === 'send' ? 'Starting...' : `Yes, send to ${preview.count}`}
</button>
<button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => setConfirming(false)}>Cancel</button>
</div>
</div>
) : (
<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
<button className="btn btn-ghost btn-sm" disabled={!!busy || !campaignKey.trim()} onClick={handlePreview}>
{busy === 'preview' ? 'Checking...' : '👀 Preview'}
</button>
<button className="btn btn-ghost btn-sm" disabled={!!busy || !canSendContent} onClick={handleTest}>
{busy === 'test' ? 'Sending...' : '✉️ Send test to hello@hireitnow.au'}
</button>
<button
className="btn btn-primary btn-sm"
disabled={!!busy || !canSendContent || !preview || preview.count === 0 || preview.running}
onClick={() => setConfirming(true)}
title={!preview ? 'Run Preview first' : ''}
>
🚀 Send to all{preview ? ` (${preview.count})` : ''}
</button>
</div>
)}
</div>
</div>
)
}
